import Seo from '@/components/Seo';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Award } from 'lucide-react';

const AuthorPage = () => {
  const accolades = [
    'Founder & Director — Portal of Commencement Midpoint Shelter (POCMS), Atlanta',
    'Founder & Owner — Ink & Innovation Printworks Publishing',
    'B.S. Electrical Engineering — Lincoln College',
    'Novelist, Playwright & Music Engineer',
  ];

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="author-page">
      <Seo title="About Author Kavi Ocktiiv" description="Meet Kavi Ocktiiv, author of the Hidden Empire and Kings Without Masters series — bold, historically-rich literary fiction." />
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 mb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="relative">
              <img
                src="https://customer-assets-agu9un31.emergentagent.net/job_e293d543-b110-4e1a-b256-18d454b4a060/artifacts/tm505qh6_suKgT.jpg"
                alt="Kavi Ocktiiv"
                className="w-full aspect-[3/4] object-cover"
              />
              <div className="absolute -bottom-4 -right-4 w-48 h-48 border border-gold/30" />
              <div className="absolute -top-4 -left-4 w-24 h-24 border border-gold/20" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            <p className="overline text-gold">The Author</p>
            <h1 className="text-4xl lg:text-5xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
              Kavi Ocktiiv
            </h1>
            <p className="text-white/60 leading-relaxed text-lg">
              Kavi Ocktiiv (Kah-vee ak-tev) is a proud Louisiana native whose heritage runs deep in American 
              soil. Tracing his lineage, he uncovered extraordinary roots reaching back to 1576 with his 12th 
              great-grandmother, Hugette Vignaud, who arrived in New Orleans in 1582. This powerful discovery 
              ignited a passionate journey of historical exploration, revealing his family's profound Indigenous 
              connections to the land—an ancestral legacy that continues to shape his life and creative mission.
            </p>
            <p className="text-white/60 leading-relaxed">
              A dedicated scholar, Kavi earned his Bachelor of Science in Electrical Engineering from Lincoln 
              College after studying at Grambling State University and Louisiana Tech University.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Bio Continued */}
      <section className="bg-[#0a0a0a] py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="prose max-w-none"
          >
            <p className="text-white/60 leading-relaxed text-lg mb-8">
              As the founder, owner, and director of Portal of Commencement Midpoint Shelter, Inc. (POCMS) 
              in Atlanta, Georgia, Kavi has built a transformative sanctuary for women and children. He 
              personally writes approximately 80% of the grants that fund the shelter, securing vital 
              resources that provide safe housing for up to six months alongside empowering workshops in 
              literacy, computer skills, job placement, and financial literacy. His hands-on leadership has 
              helped countless families rebuild their lives with dignity and hope.
            </p>
            <p className="text-white/60 leading-relaxed text-lg mb-8">
              Driven by a profound passion for storytelling, Kavi pursued novel writing and playwriting in 
              2020. He is also an accomplished musical artist and music engineer who has collaborated with 
              many of the world's renowned artists. Through his multifaceted creative work in television, 
              music, and literature, he crafts compelling narratives that resonate across mediums.
            </p>
            <p className="text-white/60 leading-relaxed text-lg">
              As founder and owner of Ink and Innovation Printworks Publishing Company, Kavi champions 
              independent voices and historically marginalized stories. At the heart of everything he creates 
              is a deep fascination with memory, omission, and cultural inheritance—bridging past and present 
              to engineer not just circuits or soundscapes, but lasting change, connection, and cultural 
              resurrection.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Accolades */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <p className="overline text-gold mb-4">Recognition</p>
            <h2 className="text-3xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
              Highlights
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {accolades.map((accolade, index) => (
              <motion.div
                key={accolade}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="card-surface p-6 flex items-start gap-4"
              >
                <Award size={20} className="text-gold flex-shrink-0 mt-1" />
                <p className="text-white/70 text-sm">{accolade}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-semibold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Explore Kavi's Works
            </h2>
            <p className="text-white/60 mb-10">
              Discover the full collection of novels from Kavi Ocktiiv.
            </p>
            <Link
              to="/books"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="view-books-btn"
            >
              View All Books
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Contact for Press */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="overline text-gold mb-4">Press & Media</p>
            <h2 className="text-3xl font-semibold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Interview & Speaking Requests
            </h2>
            <p className="text-white/60 mb-10">
              For press inquiries, interview requests, or speaking engagements, please contact 
              our publicity team.
            </p>
            <Link
              to="/contact"
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="contact-press-btn"
            >
              Contact Us
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AuthorPage;
